import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, ProgressBar, CloseButton } from 'react-bootstrap';
import { useRecoilState } from 'recoil';
import { ENDPOINTS } from '../utils/EndPoint';
import QuestionItem from '../components/globals/QuestionItem';
import AccuracyItem from '../components/globals/AccuracyItem';
import CustomPrimaryButton from '../components/globals/CustomPrimaryButton';
import { apiPost } from '../utils/Api';
import { isLoaderState, snakeBarState } from '../store/RecoilStore';
import getGif from '../utils/Helper';


const QuestQuestionAnswers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedQuest, ocean, stages } = location.state;
  const [stepForm, setStepForm] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [accuracy, setAccuracy] = useState(0);
  const [coins, setCoins] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [questLevel, setQuestLevel] = useState(null); 
  const [isLoaderInfo, setIsLoaderInfo] = useRecoilState(isLoaderState);
  const [snackBarInfo, setSnackBarInfo] = useRecoilState(snakeBarState);

  const [showAnimation, setShowAnimation] = useState(false);
  const [animationSrc, setAnimationSrc] = useState('');
  const [showButton, setShowButton] = useState(false);

  const loadQuestions = async (quest) => {
    setIsLoaderInfo(true);
    const formData = {
      ocean_id: ocean?.id,
      level: quest?.level,
      type: "quest",
      quest_id: quest?.id,
    };
    apiPost(
      ENDPOINTS.GetQuestQuestionAnswers,
      formData,
      (res) => {
        setQuestions(res?.data || []);
        setIsLoaderInfo(false);
      },
      (err) => {
        console.error("Error fetching questions:", err);
        setIsLoaderInfo(false);
      }
    );
  };


  useEffect(() => {
    if (selectedQuest) {
      loadQuestions(selectedQuest);
      setQuestLevel(selectedQuest.level);
    }
  }, [selectedQuest]);

  const handleAnswer = (item) => {
    setSelectedAnswer(item);
  };

  const handleQuestion = () => {
    if (selectedAnswer) {
      const body = {
        question_id: selectedAnswer?.question_id,
        answer_id: selectedAnswer?.answer_id,
        ocean_id: selectedQuest?.ocean_id,
        quest_id: selectedQuest.id,
        type: "quest",
      };
      setIsLoaderInfo(true);
      apiPost(
        ENDPOINTS.AddQuestionAnswersQuest,
        body,
        (res) => {

          const isCorrect = res?.data?.is_correct;
          console.log(res?.data);
          
          // Get the appropriate GIF
          const gif = getGif({
            fromStage: false,
            fromQuest: true,
            fromChallenge: false,
            stageId: selectedAnswer?.stage_id,
            level: questLevel,
            isCorrect,
          });

          setAnimationSrc(gif);
          setShowAnimation(true);
          setShowButton(true);

          if (questionIndex + 1 === questions.length) {
            completeQuest();
            setStepForm(1);
          } else {
            setQuestionIndex(questionIndex + 1);
            setSelectedAnswer(null);
          }
          setIsLoaderInfo(false);
        },
        (err) => {
          console.log("Error submitting answer", err);
          setIsLoaderInfo(false);
        }
      );
    } else {
      setSnackBarInfo({
        snackStatus: true,
        snackColor: "bg-danger",
        snackMsg: "Please select an answer",
      });
    }
  };

  const completeQuest = async () => {
    const payload = {
      ocean_id: ocean?.id,
      time: 10,
      quest_id: selectedQuest?.id,
    };
    apiPost(
      ENDPOINTS.CompleteQuest,
      payload,
      (res) => {
        if (res?.data) {
          setAccuracy(parseFloat(res.data.accuracy).toFixed(2));
          setCoins(parseFloat(res.data.quest.reward_price).toFixed(2));
        } else {
          setAccuracy(0);
          setCoins(0);
        }
        setStepForm(1);
      },
      (err) => {
        console.log("Error:", err);
        setAccuracy(0);
        setCoins(0);
      }
    );
  };

  const progressPercentage = ((questionIndex + 1) / questions.length) * 100;

  return (
    <>
      <Container fluid className='p-0 custom-welcome d-flex flex-column align-items-center justify-content-center'>
        <Row className="g-0 justify-content-center">
          <Col md={12} lg={12} className="align-items-center">

            {stepForm === 0 && (
              <>

                <div className='d-flex flex-start align-items-center'>
                  <CloseButton className="me-2" onClick={() => navigate(`/admin/quest`)} />
                  <ProgressBar
                    now={progressPercentage}
                    className="custom-progress-bar-quest my-3 flex-grow-1"
                    variant="primary"
                    label={`${progressPercentage.toFixed(0)}%`}
                  />
                  <div className="ms-2 text-white class-question-count">{`${questionIndex + 1}/${questions.length}`}</div>
                </div>

                {questions.length > 0 && questions.map((item, ind) => (
                  questionIndex === ind && (
                    <Row key={ind}>
                      <Col md={12} lg={12}>
                        <h3 className="skranji-bold text-white">{item.question}</h3>
                      </Col>
                      <Col md={12} lg={12}>
                        {item.answers.map((answer, ind2) => (
                          <QuestionItem
                            key={ind2}
                            text={answer.answer}
                            onClick={() => handleAnswer({
                              question_id: item.id,
                              answer_id: answer.id,
                            })}
                            isSelected={selectedAnswer?.answer_id === answer.id}
                          />
                        ))}
                      </Col>
                    </Row>
                  )
                ))}
              </>
            )}

            {stepForm === 1 && (
              <div className="container text-center">
                <p className="font-22 mt-4 text-white">Nice job reaching your goal!</p>
                <AccuracyItem welcomeicon="bulb" customText="Accuracy" text={`${accuracy || 0}%`} />
                <AccuracyItem welcomeicon="bulb" customText="Coins" text={`${coins || 0}`} />
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <div className='welcome-bottom-box container-fluid'>
        {/* Continue Button for Step 0 (Question Answering) */}
        {stepForm === 0 && (
          <CustomPrimaryButton
            btnTextPro="Continue"
            handleOnClick={handleQuestion}
            classNamePro="btn-custom-welcome"
            customWelcomeClass="custom-welcome-class"
          />
        )}

        {/* Continue Button for Step 1 (Completion) */}
        {stepForm === 1 && (
          <CustomPrimaryButton
            btnTextPro="Continue"
            handleOnClick={() => navigate('/admin/quest')}
            classNamePro="btn-custom-welcome"
            customWelcomeClass="custom-welcome-class"
          />
        )}

        {showAnimation && (
          <div className="popup-overlay">
            <div className="popup-container">
              <img src={animationSrc} alt="Result Animation" className="animation-gif" />
              {showButton && (
                <CustomPrimaryButton
                  btnTextPro="Continue"
                  classNamePro="custom-progress-btn-animation"
                  handleOnClick={() => {
                    setShowAnimation(false);
                  }}
                />
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default QuestQuestionAnswers;
